import { useEffect, useState } from "react";
import {
    Box,
    Grid,
    Typography,
    Checkbox,
    FormControlLabel,
    Paper,
    FormLabel,
    TextField,
    Button,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    Dialog
} from "@mui/material";
import { PlusOutlined } from "@ant-design/icons";
import CustomAlert from "src/Components/Alerts";
import Spinner from "src/Components/Spinner";
import { createNotification, deleteNotification, getNotifications } from "src/Services/notification_api";
import TableToolbar from "src/Components/TableToolbar";
import { MoreVert } from "@mui/icons-material";
import Dialogbox from "src/Components/Dialogbox";

interface ChildItem {
    id: string;
    value: string;
}

interface CategoryItem {
    [category: string]: ChildItem[];
}

interface SelectedItem {
    category: string;
    id: string;
    value: string;
}

export default function AdminNotifications() {

    const data: CategoryItem[] = [
        {
            "Fast track A/L": [
                { id: "1", value: "Arts Stream" },
                { id: "2", value: "Commerce Stream" },
                { id: "3", value: "Technology Stream" },
                { id: "4", value: "General English in A/L" }
            ]
        },
        {
            "Diploma": [
                { id: "5", value: "Diploma in Marketing" },
                { id: "6", value: "Diploma in HRM" },
                { id: "7", value: "Diploma in English" },
                { id: "8", value: "Diploma in ICT" }
            ]
        },
        {
            "Certificate course": [
                { id: "9", value: "Certificate course in Marketing" },
                { id: "10", value: "Certificate course in HRM" },
                { id: "11", value: "Certificate course in English" },
                { id: "12", value: "Certificate course in ICT" }
            ]
        }
    ];

    const [selectedParents, setSelectedParents] = useState<Record<string, boolean>>({});
    const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertDescription, setAlertDescription] = useState("");
    const [alertType, setAlertType] = useState<"success" | "error" | "warning">("success");
    const [notifications, setNotifications] = useState<any[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [openModal, setOpenModal] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [selectedNotice, setSelectedNotice] = useState<any>(null);

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const userId = sessionStorage.getItem("adminId") || "";
    const token = sessionStorage.getItem("token") || "";

    const showAlert = (description: string, type: "success" | "error" | "warning") => {
        setAlertDescription(description);
        setAlertType(type);
        setAlertOpen(true);
    };

    const handleParentCheck = (category: string, checked: boolean, children: ChildItem[]) => {
        setSelectedParents((prev) => ({ ...prev, [category]: checked }));

        if (checked) {
            const newItems: SelectedItem[] = children.map((child) => ({
                category,
                id: child.id,
                value: child.value,
            }));

            setSelectedItems((prev) => [
                ...prev.filter((x) => x.category !== category),
                ...newItems,
            ]);
        } else {
            setSelectedItems((prev) => prev.filter((x) => x.category !== category));
        }
    };

    const handleChildCheck = (category: string, child: ChildItem, checked: boolean) => {
        if (checked) {
            setSelectedItems((prev) => [...prev, { category, id: child.id, value: child.value }]);
        } else {
            setSelectedItems((prev) => prev.filter((x) => x.id !== child.id));
        }
    };

    const handleAddNotice = async () => {
        setIsLoading(true);
        const body = {
            userId: userId,
            userType: 3,
            title: title,
            message: description,
            facultyCombinationIds: selectedItems.map((item) => item.id),
        }

        try {
            await createNotification(body, token);
            showAlert("Notification added successfully", "success");
            setTitle("");
            setDescription("");
            setSelectedItems([]);
            setSelectedParents({});
            setIsLoading(false);
            handleGetNotifications();
        } catch (error) {
            showAlert("Failed to add notification", "error");
            setIsLoading(false);
        }
    }

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>, notice: any) => {
        // Implement menu click logic here
        setAnchorEl(event.currentTarget);
        setSelectedNotice(notice);
    };

    const handleGetNotifications = async () => {
        try {
            const res = await getNotifications(token);
            setNotifications(res);
        } catch (error) {
            setNotifications([]);
        }
    }

    useEffect(() => {
        handleGetNotifications();
    }, []);

    const filteredNotifications = notifications.filter((notice) => {
        const term = searchTerm.toLowerCase();

        return (
            notice.title?.toLowerCase().includes(term) ||
            notice.message?.toLowerCase().includes(term)
        );
    });

    const handleDelete = async (noticeId: string) => {
        setIsLoading(true);

        try {
            await deleteNotification(noticeId, userId, "3", token);
            showAlert("Notification deleted successfully", "success");
            setIsLoading(false);
            setIsOpen(false);
            handleGetNotifications();
            handleMenuClose();
        } catch (error) {
            showAlert("Failed to delete notification", "error");
            setIsLoading(false);
            setIsOpen(false);
        }
    }

    return (
        <div className="comman-outer">
            <CustomAlert
                open={alertOpen}
                onClose={() => setAlertOpen(false)}
                description={alertDescription}
                type={alertType}
                timeout={3000}
            />
            <Spinner isLoading={isLoading} />

            <div className="table-title-outer">
                <div className="table-title">Add New Notification</div>
            </div>
            <div className="space-bar"></div>
            <div className="card-outer" style={{ padding: "20px" }}>
                <Paper elevation={1} sx={{ padding: 1, width: "100%", backgroundColor: "black" }}>
                    <Grid container spacing={3}>
                        {data.map((block, idx) => {
                            const category = Object.keys(block)[0];
                            const children = block[category];
                            const parentChecked = selectedParents[category] || false;

                            return (
                                <Grid item xs={12} md={6} lg={4} key={idx}>
                                    {/* Parent Checkbox */}
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={parentChecked}
                                                sx={{
                                                    color: "#e3d732b4",
                                                    "&.Mui-checked": { color: "#e3d732b4" }
                                                }}
                                                onChange={(e) =>
                                                    handleParentCheck(
                                                        category,
                                                        e.target.checked,
                                                        children
                                                    )
                                                }
                                            />
                                        }
                                        label={
                                            <Typography fontWeight="bold" fontSize="16px" sx={{ color: "#FFFFFF" }}>
                                                {category}
                                            </Typography>
                                        }
                                    />

                                    {/* Children */}
                                    {parentChecked && (
                                        <Grid container spacing={1} mt={1}>
                                            {children.map((child) => (
                                                <Grid item xs={12} key={child.id}>
                                                    <FormControlLabel
                                                        sx={{ color: "#FFFFFF" }}
                                                        control={
                                                            <Checkbox
                                                                sx={{
                                                                    color: "#e3d732b4",
                                                                    "&.Mui-checked": { color: "#e3d732b4" }
                                                                }}
                                                                checked={selectedItems.some(
                                                                    (x) => x.id === child.id
                                                                )}
                                                                onChange={(e) =>
                                                                    handleChildCheck(
                                                                        category,
                                                                        child,
                                                                        e.target.checked
                                                                    )
                                                                }
                                                            />
                                                        }
                                                        label={child.value}
                                                    />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    )}
                                </Grid>
                            );
                        })}
                    </Grid>
                </Paper>
            </div>
            <div className="card-outer">
                <Grid container spacing={2} style={{ padding: "16px" }} className="form-grid ">
                    <Grid item xs={12}>
                        <FormLabel component="legend" sx={{ color: "#3d3d3dff", mb: 1 }}>Notification Title</FormLabel>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Notification Title"
                            size="small"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            sx={{
                                backgroundColor: "black",
                                input: {
                                    color: "#ffffff",
                                    "::placeholder": {
                                        color: "grey",
                                        opacity: 1,
                                    },
                                },
                                "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                        borderColor: "#555",
                                    },
                                    "&:hover fieldset": {
                                        borderColor: "#aaa",
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: "gray",
                                    },
                                },
                            }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <FormLabel component="legend" sx={{ color: "#3d3d3dff", mb: 1 }}>Notification Description</FormLabel>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Notification Description"
                            size="small"
                            multiline
                            rows={4}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            sx={{
                                backgroundColor: "black",
                                "& textarea": {
                                    color: "#ffffff",                // text color
                                },
                                "& textarea::placeholder": {
                                    color: "grey",                   // placeholder
                                    opacity: 1,
                                },
                                "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                        borderColor: "#555",
                                    },
                                    "&:hover fieldset": {
                                        borderColor: "#aaa",
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: "gray",
                                    },
                                },
                            }}
                        />
                    </Grid>
                </Grid>
            </div>
            <div className="pagination-footer">
                <div></div>

                <Button
                    className="primary-btn"
                    variant="contained"
                    size="small"
                    onClick={handleAddNotice}
                    startIcon={<PlusOutlined style={{ fontSize: "14px", color: "black" }} />}
                    disabled={!title || !description || selectedItems.length === 0}
                >
                    Add Notification
                </Button>
            </div>

            <Divider style={{ margin: "24px 0", borderColor: "#444" }} />

            <div>
                <TableToolbar
                    title="Notifications"
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    isButton={false}
                    buttonText="Notice"
                    onButtonClick={() => setOpenModal(true)}
                    buttonIcon={<PlusOutlined style={{ fontSize: "14px", color: "black" }} />}
                />
                <div className="space-bar"></div>
            </div>

            {filteredNotifications.length > 0 ?
                [...filteredNotifications]
                    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                    .map((notice) => (
                        <div key={notice.id} className="card-outer" style={{ marginBottom: "16px" }}>
                            <div className="card-outer1">
                                <p className="t-card">Title: {notice.title}</p>
                                <p className="t-card">
                                    Description: {notice.message.length > 20 ? `${notice.message.substring(0, 20)}...` : notice.message}
                                </p>
                                <p className="t-card">Date: {notice.createdAt.split("T")[0]}</p>
                                <p className="t-card">Time: {notice.createdAt.split("T")[1].split(".")[0]}</p>
                            </div>

                            <IconButton onClick={(e) => handleMenuClick(e, notice)}>
                                <MoreVert style={{ color: "white" }} />
                            </IconButton>
                        </div>
                    ))
                : (
                    <div className="no-results" style={{ padding: "16px", textAlign: "center" }}>
                        <p>No notifications found.</p>
                    </div>
                )
            }

            <div className="pagination-footer"></div>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <MenuItem onClick={() => setViewDialogOpen(true)}>View</MenuItem>
                <MenuItem onClick={() => setIsOpen(true)}>Delete</MenuItem>
            </Menu>

            <Dialog
                open={viewDialogOpen}
                onClose={() => {
                    setViewDialogOpen(false)
                    handleMenuClose();
                }}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: { backgroundColor: "#c6c2c2e3", color: "white", paddingBottom: 2, borderRadius: 2, boxShadow: 24, border: "1px solid #ffffffff" },
                }}
            >
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", borderBottom: "1px solid #555", backgroundColor: "#393939", padding: "25px 16px" }}>
                    <h3>Notification Details</h3>
                </div>

                <Grid container spacing={2} style={{ padding: "25px 16px", color: "white" }} className="form-grid">
                    <Grid item xs={12}>
                        <strong>Title:</strong> {selectedNotice?.title}
                    </Grid>
                    <Grid item xs={12}>
                        <strong>Description:</strong> {selectedNotice?.message}
                    </Grid>
                    <Grid item xs={12}>
                        <strong>Date:</strong> {selectedNotice?.createdAt.split("T")[0]}
                    </Grid>
                    <Grid item xs={12}>
                        <strong>Time:</strong> {selectedNotice?.createdAt.split("T")[1].split(".")[0]}
                    </Grid>

                    <Grid item xs={12}>
                        <strong>Faculties:</strong>
                        <div style={{
                            marginTop: "8px",
                            padding: "10px",
                            background: "#e8e8e8",
                            borderRadius: "6px"
                        }}>
                            {selectedNotice?.faculties?.length > 0 ? (
                                selectedNotice.faculties.map((f: any, index: number) => (
                                    <div key={index} style={{ marginBottom: "6px" }}>
                                        <strong>{f.programme}</strong> – {f.stream}
                                    </div>
                                ))
                            ) : (
                                <div>No faculties found</div>
                            )}
                        </div>
                    </Grid>
                </Grid>
            </Dialog>

            <Dialogbox
                open={isOpen}
                title="Delete Notification"
                content="Are you sure you want to delete this notification?"
                agreeButtonText="Delete"
                disagreeButtonText="Cancel"
                onAgree={() => handleDelete(selectedNotice.id)}
                onDisagree={() => setIsOpen(false)}
                onClose={() => setIsOpen(false)}
            />
        </div>
    );
}
